import { useEffect, useRef } from 'react';
import { analytics } from '../utils/analytics/AnalyticsService';

interface UseVideoAnalyticsProps {
  videoId: string;
  duration?: number;
  isPlaying: boolean;
  currentTime: number;
}

const WATCH_TIME_INTERVAL = 5000; // Report watch time every 5 seconds

export const useVideoAnalytics = ({
  videoId,
  duration,
  isPlaying,
  currentTime,
}: UseVideoAnalyticsProps) => {
  const watchTimeIntervalRef = useRef<number>();
  const lastReportedTimeRef = useRef(currentTime);

  useEffect(() => {
    // Track video start
    analytics.trackVideoEvent({
      category: 'Video',
      action: 'load',
      videoId,
      timestamp: Date.now(),
      duration,
      currentTime: 0,
    });

    return () => {
      // Track video end on unmount
      analytics.trackVideoEvent({
        category: 'Video',
        action: 'unload',
        videoId,
        timestamp: Date.now(),
        duration,
        currentTime,
      });
    };
  }, [videoId, duration, currentTime]);

  useEffect(() => {
    const reportWatchTime = () => {
      const watchedSeconds = currentTime - lastReportedTimeRef.current;
      if (watchedSeconds > 0 && duration) {
        analytics.trackWatchTime({
          category: 'Video',
          action: 'watch_time',
          videoId,
          timestamp: Date.now(),
          duration,
          currentTime,
          watchedSeconds,
          percentageWatched: (currentTime / duration) * 100,
        });
        lastReportedTimeRef.current = currentTime;
      }
    };

    if (isPlaying) {
      // Start tracking watch time
      analytics.trackVideoEvent({
        category: 'Video',
        action: 'play',
        videoId,
        timestamp: Date.now(),
        duration,
        currentTime,
      });

      watchTimeIntervalRef.current = window.setInterval(reportWatchTime, WATCH_TIME_INTERVAL);
    } else {
      // Stop tracking and report final watch time
      if (watchTimeIntervalRef.current) {
        clearInterval(watchTimeIntervalRef.current);
        reportWatchTime();
      }

      analytics.trackVideoEvent({
        category: 'Video',
        action: 'pause',
        videoId,
        timestamp: Date.now(),
        duration,
        currentTime,
      });
    }

    return () => {
      if (watchTimeIntervalRef.current) {
        clearInterval(watchTimeIntervalRef.current);
      }
    };
  }, [isPlaying, videoId, currentTime, duration]);
}; 
